/**
 * DefaultRedirect — catch-all routing policy for unmatched paths.
 *
 * Kept in components/ (not utils/) because this is a React component, and
 * separated from AnalyticsRedirect because these serve different concerns:
 * - DefaultRedirect: auth-aware fallback for any unmatched URL
 * - AnalyticsRedirect: legacy URL migration for a specific known route
 *
 * Separation note: this is routing *policy* (what to do for unmatched paths),
 * while PublicRoute and ProtectedRoute are access-control *mechanisms* (who
 * may see a given page).  Keeping them in separate files makes each
 * responsibility independently readable and testable.
 *
 * Stale-token cleanup is handled by the startup useEffect in App.js.
 * isAuthenticated() is a pure predicate — safe to call during render.
 */
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import authService from "../services/auth";
import { ROUTES } from "../config/constants";

/**
 * Catch-all redirect for unmatched routes.
 * Authenticated users go to their dashboard; unauthenticated users go to the
 * landing page.
 */
export function DefaultRedirect() {
  const location = useLocation();
  if (process.env.NODE_ENV === "development") {
    // Routing gap: no <Route> in App.js matched this path.  Log it so the
    // problem is visible immediately rather than silently redirecting.
    // eslint-disable-next-line no-console
    console.warn(
      `[DefaultRedirect] No route matched "${location.pathname}" — check App.js route list`,
    );
  } else if (typeof window !== "undefined" && window.errorTracker) {
    // Report unmatched routes to telemetry in production so routing gaps
    // surface in monitoring rather than silently redirecting users.
    // window.errorTracker must expose captureMessage(msg): void.
    // Initialised by the application shell before any components mount.
    window.errorTracker.captureMessage(
      `[DefaultRedirect] No route matched "${location.pathname}"`,
    );
  }
  if (authService.isAuthenticated()) {
    return <Navigate to={ROUTES.AUTHENTICATED_HOME} replace />;
  }
  return <Navigate to={ROUTES.LANDING} replace />;
}
